import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import { Link } from 'react-router-dom';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.profitos.pro';

interface Offer {
  id: string;
  name: string;
  merchantName: string | null;
  category: string | null;
  niche: string | null;
  commissionType: string;
  commissionValue: number | null;
  overallScore: number | null;
  trendingScore: number | null;
  competitionDensity: number | null;
  profitabilityScore: number | null;
  status: string;
  description: string | null;
  imageUrl: string | null;
  verified: boolean;
  featured: boolean;
}

interface OffersResponse {
  success: boolean;
  data?: {
    offers?: Offer[];
    pagination?: {
      total: number;
      hasMore: boolean;
    };
  };
}

export function MarketplacePage(): ReactNode {
  const { accessToken, tenant } = useAuthStore();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [niche, setNiche] = useState('');
  const [sortBy, setSortBy] = useState<'overall' | 'trending' | 'profitability' | 'newest'>('overall');
  const [showVerifiedOnly, setShowVerifiedOnly] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['marketplace-offers', search, category, niche, sortBy, showVerifiedOnly],
    queryFn: async (): Promise<OffersResponse> => {
      const params = new URLSearchParams();
      if (search !== '') { params.set('search', search); }
      if (category !== '') { params.set('category', category); }
      if (niche !== '') { params.set('niche', niche); }
      params.set('sortBy', sortBy);
      params.set('limit', '24');
      if (showVerifiedOnly) { params.set('verified', 'true'); }

      const res = await fetch(`${API_URL}/offers?${String(params)}`, {
        headers: {
          Authorization: `Bearer ${accessToken ?? ''}`,
          ...(tenant?.id ? { 'X-Tenant-ID': tenant.id } : {}),
        },
      });

      if (!res.ok) { throw new Error('Failed to fetch offers'); }
      return (await res.json()) as OffersResponse;
    },
  });

  const offers: Offer[] = data?.data?.offers ?? [];
  const totalOffers = data?.data?.pagination?.total ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Affiliate Marketplace</h2>
          <p className="text-gray-500">
            Discover high-converting offers from verified merchants
          </p>
        </div>
        <Link
          to="/marketplace/merchant"
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          List Your Offer
        </Link>
      </div>

      {/* Stats Bar */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-lg border bg-white p-4">
          <div className="text-sm text-gray-500">Total Offers</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{totalOffers.toLocaleString()}</div>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <div className="text-sm text-gray-500">Verified Merchants</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">
            {offers.filter(o => o.verified).length}
          </div>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <div className="text-sm text-gray-500">Categories</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">12+</div>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <div className="text-sm text-gray-500">Avg. Commission</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">
            {offers.length > 0
              ? `${Math.round(offers.reduce((sum, o) => sum + (o.commissionValue ?? 0), 0) / offers.length)}%`
              : '—'}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border bg-white p-4">
        <div className="grid gap-4 md:grid-cols-5">
          <input
            type="text"
            placeholder="Search offers..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); }}
            className="rounded-md border border-gray-300 px-4 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <select
            value={category}
            onChange={(e) => { setCategory(e.target.value); }}
            className="rounded-md border border-gray-300 px-4 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">All Categories</option>
            <option value="health">Health & Wellness</option>
            <option value="finance">Finance</option>
            <option value="software">Software</option>
            <option value="ecommerce">E-commerce</option>
            <option value="education">Education</option>
            <option value="travel">Travel</option>
            <option value="fitness">Fitness</option>
          </select>
          <select
            value={niche}
            onChange={(e) => { setNiche(e.target.value); }}
            className="rounded-md border border-gray-300 px-4 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">All Niches</option>
            <option value="saas">SaaS</option>
            <option value="crypto">Cryptocurrency</option>
            <option value="health">Health Supplements</option>
            <option value="courses">Online Courses</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value as typeof sortBy); }}
            className="rounded-md border border-gray-300 px-4 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="overall">Best Overall</option>
            <option value="trending">Trending</option>
            <option value="profitability">Highest Commission</option>
            <option value="newest">Newest</option>
          </select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showVerifiedOnly}
              onChange={(e) => { setShowVerifiedOnly(e.target.checked); }}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Verified only</span>
          </label>
        </div>
      </div>

      {/* Featured Offers */}
      {offers.filter(o => o.featured).length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Featured Offers</h3>
          <div className="grid gap-4 md:grid-cols-3">
            {offers.filter(o => o.featured).slice(0, 3).map((offer) => (
              <FeaturedOfferCard key={offer.id} offer={offer} />
            ))}
          </div>
        </div>
      )}

      {/* Offers Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="animate-pulse rounded-lg border bg-white p-6">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-red-600">Failed to load offers. Please try again.</p>
        </div>
      ) : offers.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No offers found</h3>
          <p className="mt-2 text-gray-500">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {offers.length} of {totalOffers} offers
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {offers.map((offer) => (
              <OfferCard key={offer.id} offer={offer} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function FeaturedOfferCard({ offer }: { offer: Offer }): ReactNode {
  return (
    <Link
      to={`/offers/${offer.id}`}
      className="group relative rounded-lg border-2 border-primary-500 bg-white p-6 shadow-md hover:shadow-lg transition-shadow"
    >
      {offer.imageUrl && (
        <div className="mb-4 h-32 w-full overflow-hidden rounded-lg bg-gray-100">
          <img src={offer.imageUrl} alt={offer.name} className="h-full w-full object-cover" />
        </div>
      )}
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-primary-600">{offer.name}</h3>
        {offer.verified && (
          <svg className="h-5 w-5 text-green-500 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-3">{offer.merchantName ?? 'Unknown merchant'}</p>
      <div className="flex items-center justify-between">
        <span className="text-lg font-bold text-primary-600">
          {offer.commissionType === 'percentage'
            ? `${String(offer.commissionValue ?? 0)}%`
            : `$${String(offer.commissionValue ?? 0)}`}
        </span>
        {offer.overallScore !== null && (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            offer.overallScore >= 80 ? 'bg-green-100 text-green-800' :
            offer.overallScore >= 60 ? 'bg-yellow-100 text-yellow-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            Score: {offer.overallScore.toFixed(0)}
          </span>
        )}
      </div>
    </Link>
  );
}

function OfferCard({ offer }: { offer: Offer }): ReactNode {
  return (
    <Link
      to={`/offers/${offer.id}`}
      className="group rounded-lg border bg-white p-6 hover:shadow-md transition-shadow"
    >
      {offer.imageUrl && (
        <div className="mb-4 h-24 w-full overflow-hidden rounded-lg bg-gray-100">
          <img src={offer.imageUrl} alt={offer.name} className="h-full w-full object-cover" />
        </div>
      )}
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-primary-600">{offer.name}</h3>
        {offer.verified && (
          <svg className="h-5 w-5 text-green-500 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-3">{offer.merchantName ?? 'Unknown merchant'}</p>
      <div className="flex items-center justify-between">
        <span className="text-lg font-bold text-primary-600">
          {offer.commissionType === 'percentage'
            ? `${String(offer.commissionValue ?? 0)}%`
            : `$${String(offer.commissionValue ?? 0)}`}
        </span>
        {offer.overallScore !== null && (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            offer.overallScore >= 80 ? 'bg-green-100 text-green-800' :
            offer.overallScore >= 60 ? 'bg-yellow-100 text-yellow-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {offer.overallScore.toFixed(0)}
          </span>
        )}
      </div>
    </Link>
  );
}
